/**
 * Client-side Image Compression Service
 *
 * Compresses images before upload to reduce bandwidth and upload time.
 * Maintains quality while significantly reducing file size.
 */

import logger from '../utils/logger';

const DEFAULT_OPTIONS = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
  maxSizeKB: 500, // Target max size in KB
};

/**
 * Compress a single image file
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed image as a File object
 */
export const compressImage = async (file, options = {}) => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Skip if not an image
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // Skip if file is already small enough
  if (file.size <= opts.maxSizeKB * 1024) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > opts.maxWidth || height > opts.maxHeight) {
            const ratio = Math.min(opts.maxWidth / width, opts.maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const targetBytes = opts.maxSizeKB * 1024;

          const tryCompress = (quality) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolve(file);
                  return;
                }

                // If still over target and quality can be reduced further, try again
                if (blob.size > targetBytes && quality > 0.3) {
                  tryCompress(quality - 0.1);
                  return;
                }

                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                if (compressedFile.size >= file.size) {
                  resolve(file);
                  return;
                }

                resolve(compressedFile);
              },
              'image/jpeg',
              quality
            );
          };

          tryCompress(opts.quality);
        } catch (error) {
          logger.warn('Image compression failed, using original:', error);
          resolve(file);
        }
      };

      img.onerror = () => {
        logger.warn('Failed to load image for compression');
        resolve(file);
      };

      img.src = event.target.result;
    };

    reader.onerror = () => {
      logger.warn('Failed to read file for compression');
      resolve(file);
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Compress base64 image string
 * @param {string} base64String - Base64 image data URL
 * @param {Object} options - Compression options
 * @returns {Promise<string>} - Compressed base64 string
 */
export const compressBase64Image = async (base64String, options = {}) => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  if (!base64String || !base64String.startsWith('data:image/')) {
    return base64String;
  }

  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > opts.maxWidth || height > opts.maxHeight) {
          const ratio = Math.min(opts.maxWidth / width, opts.maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const targetBytes = (opts.maxSizeKB || 500) * 1024;
        const targetBase64Len = Math.round(targetBytes * 1.37);

        const tryCompress = (quality) => {
          const result = canvas.toDataURL('image/jpeg', quality);
          if (result.length > targetBase64Len && quality > 0.3) {
            tryCompress(quality - 0.1);
            return;
          }
          if (result.length >= base64String.length) {
            resolve(base64String);
          } else {
            resolve(result);
          }
        };

        tryCompress(opts.quality);
      } catch (error) {
        logger.warn('Base64 compression failed:', error);
        resolve(base64String);
      }
    };

    img.onerror = () => {
      resolve(base64String);
    };

    img.src = base64String;
  });
};

/**
 * Compress multiple images in parallel
 * @param {File[]} files - Array of image files
 * @param {Object} options - Compression options
 * @returns {Promise<File[]>} - Array of compressed files
 */
export const compressImages = async (files, options = {}) => {
  if (!Array.isArray(files)) return files;

  const compressionPromises = files.map((file) => compressImage(file, options));
  return Promise.all(compressionPromises);
};

/**
 * Get file size in human readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Calculate compression ratio
 * @param {number} originalSize - Original size in bytes
 * @param {number} compressedSize - Compressed size in bytes
 * @returns {number} - Compression percentage (0-100)
 */
export const getCompressionRatio = (originalSize, compressedSize) => {
  if (originalSize === 0) return 0;
  return Math.round((1 - compressedSize / originalSize) * 100);
};

export default {
  compressImage,
  compressBase64Image,
  compressImages,
  formatFileSize,
  getCompressionRatio,
};
